// Recipes at stations (decision 0053: "recipes as data at stations: a forge for weapon upgrades, a cooking pot for
// food with short buffs"). A recipe names its station, the items it takes and the currency it costs, and what it
// makes: items (a dish), or the next level of an item the crafter owns (a forge upgrade). A craft is all or nothing:
// every input there, the money there, the outputs fitting the inventory once the inputs are gone; refused, nothing
// changes. The money is a plain `Wallet` the game keeps.
import type { Inventory, InventoryState, ItemStack } from "./inventory";
import type { ItemTable } from "./items";

/** The one currency's holder (decision 0053: one currency). */
export interface Wallet {
  balance: number;
}

/** A recipe as data. */
export interface RecipeDef {
  id: string;
  /** The station kind it is made at ("forge", "pot"). */
  station: string;
  inputs: readonly ItemStack[];
  /** Currency it costs (default 0). */
  cost?: number;
  /** What it makes: items, or `level` of an item the crafter owns (from level − 1). */
  output: { items: readonly ItemStack[] } | { upgrade: string; level: number };
}

/** Why a recipe can't be made. */
export type CraftRefusal = "unknown" | "station" | "inputs" | "money" | "room" | "unowned" | "level";

/** What a craft works on. */
export interface CraftContext {
  inv: InventoryState;
  wallet: Wallet;
  /** Forge levels by item id (absent: 1): an upgrade writes here. */
  levels: Record<string, number>;
  /** The station the crafter stands at (checked when given). */
  station?: string;
}

/** What a craft did. */
export interface Crafted {
  recipe: string;
  spent: ItemStack[];
  cost: number;
  made: ItemStack[];
  upgraded: { item: string; level: number } | null;
}

/** Problems with recipes against the item table (empty when sound). */
export function checkRecipes(recipes: readonly RecipeDef[], items: ItemTable): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of recipes) {
    const at = `recipe "${r.id}"`;
    if (seen.has(r.id)) out.push(`${at} is defined twice`);
    seen.add(r.id);
    if (!r.inputs.length && !(r.cost ?? 0)) out.push(`${at} takes nothing`);
    for (const i of r.inputs) {
      if (!items.has(i.item)) out.push(`${at} takes a missing item "${i.item}"`);
      if (!Number.isInteger(i.count) || i.count < 1) out.push(`${at} takes ${i.count} of "${i.item}"`);
    }
    if (r.cost !== undefined && !(Number.isInteger(r.cost) && r.cost >= 0)) out.push(`${at} costs ${r.cost}`);
    if ("items" in r.output) {
      if (!r.output.items.length) out.push(`${at} makes nothing`);
      for (const o of r.output.items) {
        if (!items.has(o.item)) out.push(`${at} makes a missing item "${o.item}"`);
        if (!Number.isInteger(o.count) || o.count < 1) out.push(`${at} makes ${o.count} of "${o.item}"`);
      }
    } else {
      const u = r.output;
      if (!items.has(u.upgrade)) out.push(`${at} upgrades a missing item "${u.upgrade}"`);
      else if (!Number.isInteger(u.level) || u.level < 2 || u.level > items.maxLevel(u.upgrade)) out.push(`${at} upgrades "${u.upgrade}" to level ${u.level} (it has ${items.maxLevel(u.upgrade)})`);
    }
  }
  return out;
}

/** Recipes over the item table and an inventory. */
export interface Crafting {
  /** The recipes made at a station, in table order. */
  at(station: string): RecipeDef[];
  get(id: string): RecipeDef | undefined;
  /** Null when the recipe can be made now, else why not. */
  can(id: string, ctx: CraftContext): CraftRefusal | null;
  /** Make it: inputs and money spent, outputs added (or the level raised). A refusal changes nothing. */
  craft(id: string, ctx: CraftContext): Crafted | { refused: CraftRefusal };
}

/** Build crafting over the recipes. Throws listing every problem `checkRecipes` finds. */
export function createCrafting(items: ItemTable, inventory: Inventory, recipes: readonly RecipeDef[]): Crafting {
  const issues = checkRecipes(recipes, items);
  if (issues.length) throw new Error(`crafting: ${issues.join("; ")}`);
  const byId = new Map(recipes.map((r) => [r.id, r]));
  const c: Crafting = {
    at: (station) => recipes.filter((r) => r.station === station),
    get: (id) => byId.get(id),
    can(id, ctx) {
      const r = byId.get(id);
      if (!r) return "unknown";
      if (ctx.station !== undefined && ctx.station !== r.station) return "station";
      if ("upgrade" in r.output) {
        const u = r.output;
        if (inventory.count(ctx.inv, u.upgrade) < 1) return "unowned";
        if ((ctx.levels[u.upgrade] ?? 1) !== u.level - 1) return "level";
      }
      for (const i of r.inputs) if (inventory.count(ctx.inv, i.item) < i.count) return "inputs";
      if (ctx.wallet.balance < (r.cost ?? 0)) return "money";
      if ("items" in r.output && !inventory.fits(ctx.inv, r.output.items, r.inputs)) return "room";
      return null;
    },
    craft(id, ctx) {
      const why = c.can(id, ctx);
      if (why) return { refused: why };
      const r = byId.get(id)!;
      for (const i of r.inputs) inventory.take(ctx.inv, i.item, i.count);
      ctx.wallet.balance -= r.cost ?? 0;
      const out: Crafted = { recipe: id, spent: r.inputs.map((i) => ({ ...i })), cost: r.cost ?? 0, made: [], upgraded: null };
      if ("items" in r.output) {
        for (const o of r.output.items) inventory.add(ctx.inv, o.item, o.count);
        out.made = r.output.items.map((o) => ({ ...o }));
      } else {
        ctx.levels[r.output.upgrade] = r.output.level;
        out.upgraded = { item: r.output.upgrade, level: r.output.level };
      }
      return out;
    },
  };
  return c;
}
